/**
 * Dashboard / Home Page
 *
 * Personalized dashboard showing:
 * - Today's practice status and quick actions
 * - Progress metrics (streak, weekly sessions, total rituals)
 * - Quick access to practice tools and learning resources
 *
 * Note: Metadata is handled by the root layout (default title/description)
 * since this is a client component that uses React hooks.
 */

'use client';

import { useMemo } from 'react';
import { Flame, Calendar, TrendingUp } from 'lucide-react';
import { TodayPanel } from '@/components/home/TodayPanel';
import { LibraryCard } from '@/components/home/LibraryCard';
import { PracticeCard } from '@/components/home/PracticeCard';
import { MetricCard } from '@/components/home/MetricCard';
import { useWeeklyMetrics } from '@/lib/hooks/useMetrics';
import { useRitualStats } from '@/lib/hooks/useMetrics';
import { useCurrentStreak } from '@/lib/hooks/useRitual';

export default function DashboardPage() {
  const {
    data: weeklyMetrics,
    isLoading: isLoadingWeekly,
    isError: isErrorWeekly,
    refetch: refetchWeekly,
  } = useWeeklyMetrics();
  const {
    data: ritualStats,
    isLoading: isLoadingStats,
    isError: isErrorStats,
    refetch: refetchStats,
  } = useRitualStats();
  const {
    data: streak,
    isLoading: isLoadingStreak,
    isError: isErrorStreak,
    refetch: refetchStreak,
  } = useCurrentStreak();

  // Extract and normalize data
  const currentStreak = useMemo(() => (!isErrorStreak && streak) || 0, [isErrorStreak, streak]);
  const weeklySessions = useMemo(
    () => (!isErrorWeekly && weeklyMetrics?.rituals_completed) || 0,
    [isErrorWeekly, weeklyMetrics?.rituals_completed]
  );
  const weeklyMinutes = useMemo(
    () => (!isErrorWeekly && weeklyMetrics?.total_ritual_time_minutes) || 0,
    [isErrorWeekly, weeklyMetrics?.total_ritual_time_minutes]
  );
  const totalRituals = useMemo(
    () => (!isErrorStats && ritualStats?.total_rituals) || 0,
    [isErrorStats, ritualStats?.total_rituals]
  );

  // Calculate "next milestone" - sessions until week 2
  const sessionsUntilWeek2 = useMemo(
    () => (weeklySessions < 14 ? 14 - weeklySessions : 0),
    [weeklySessions]
  );

  // Determine if user is new (no rituals yet)
  const isNewUser = useMemo(() => totalRituals === 0, [totalRituals]);

  // Memoized subtitle generators
  const streakSubtitle = useMemo(() => {
    if (currentStreak === 0) return 'Get started to unlock';
    if (currentStreak === 1) return 'Great start!';
    return 'Keep it going';
  }, [currentStreak]);

  const weeklySubtitle = useMemo(() => {
    if (weeklySessions === 0) return 'Get started to unlock';
    if (weeklyMinutes > 0) return `${weeklyMinutes} min practiced`;
    return `${weeklySessions} session${weeklySessions !== 1 ? 's' : ''} this week`;
  }, [weeklySessions, weeklyMinutes]);

  const totalSubtitle = useMemo(() => {
    if (totalRituals === 0) return 'Get started to unlock';
    if (sessionsUntilWeek2 > 0) return `${sessionsUntilWeek2} until Week 2 review`;
    return 'Keep practicing';
  }, [totalRituals, sessionsUntilWeek2]);

  return (
    <main className="space-y-4 sm:space-y-5 md:space-y-6 lg:space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 w-full">
      {/* Today Panel - Your Dojo Cockpit */}
      <TodayPanel />

      {/* Personalized Metrics - Progress Strip */}
      <section
        aria-label="Your progress metrics"
        className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 md:gap-4 lg:gap-5"
      >
        <MetricCard
          icon={Flame}
          value={currentStreak}
          label="Day Streak"
          subtitle={streakSubtitle}
          isLoading={isLoadingStreak}
          isError={isErrorStreak}
          onRetry={refetchStreak}
        />
        <MetricCard
          icon={Calendar}
          value={weeklySessions}
          label="This Week"
          subtitle={weeklySubtitle}
          isLoading={isLoadingWeekly}
          isError={isErrorWeekly}
          onRetry={refetchWeekly}
        />
        <MetricCard
          icon={TrendingUp}
          value={totalRituals}
          label="Total Sessions"
          subtitle={totalSubtitle}
          isLoading={isLoadingStats}
          isError={isErrorStats}
          onRetry={refetchStats}
        />
      </section>

      {/* Quick Actions - Your Practice & Learning */}
      <section
        aria-label="Quick actions"
        className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-5 lg:gap-6"
      >
        {/* Always show Practice first, then Learning Library (which includes Modules) */}
        <PracticeCard />
        <LibraryCard />
      </section>
    </main>
  );
}
